export interface ImageResizerOptions {
    maxWidth: number;
    maxHeight: number;
    quality: number;
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

function resizeCanvas(img: HTMLImageElement, maxWidth: number, maxHeight: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    if (ctx !== null) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.clearRect(0, 0, canvas.width, canvas.height); // ensures transparent background
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    }

    return canvas;
}

function canvasToFile(
    canvas: HTMLCanvasElement,
    originalName: string,
    format: "jpeg" | "png",
    quality?: number
): Promise<File> {
    return new Promise((resolve, reject) => {
        const mimeType = `image/${format}`;
        const extension = format === "jpeg" ? ".jpg" : ".png";
        const errorMessage = `Failed to create ${format.toUpperCase()} blob`;

        canvas.toBlob(
            blob => {
                if (blob === null) return reject(new Error(errorMessage));
                const newName = originalName.replace(/\.[^/.]+$/, extension);
                resolve(new File([blob], newName, { type: mimeType }));
            },
            mimeType,
            quality
        );
    });
}

function isImageTransparent(img: HTMLImageElement): boolean {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx === null) return false;

    // Use a smaller canvas for sampling - 100x100 is sufficient for transparency detection
    const sampleSize = 100;
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
    const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize).data;

    // Check alpha channel (every 4th value starting from index 3)
    for (let i = 3; i < imageData.length; i += 4) {
        if (imageData[i] < 255) return true;
    }
    return false;
}

export async function resizeImageIfNeeded(file: File, opts: ImageResizerOptions): Promise<File> {
    try {
        if (!file.type.startsWith("image/") || file.type === "image/gif") return file;

        const url = URL.createObjectURL(file);
        try {
            const img = await loadImage(url);
            const canvas = resizeCanvas(img, opts.maxWidth, opts.maxHeight);
            const hasResized = img.width !== canvas.width;
            const newImage = isImageTransparent(img)
                ? await canvasToFile(canvas, file.name, "png")
                : await canvasToFile(canvas, file.name, "jpeg", opts.quality);
            if (!hasResized && file.size <= newImage.size) {
                return file;
            }
            return newImage;
        } finally {
            URL.revokeObjectURL(url);
        }
    } catch {
        return file;
    }
}
